/**
 * NDJSON 流解析 —— 零依赖纯逻辑，唯一处理「半行 / 跨 chunk 分帧 / 空行 / 结尾无换行」的地方。
 *
 * 不碰 `fetch`、不碰 contracts 校验（校验在域模块里逐行套）。本文件只把字节流
 * 切成一个个完整的 JSON 行并 `JSON.parse`，对外暴露为 async iterable。
 */

/**
 * 把任意字符串迭代器（每次 yield 一个文本 chunk）解析为逐行 JSON 对象。
 *
 * 分帧规则：以 `\n` 为行分隔符；`\r\n` 的 `\r` 被剥除；空行（去空白后为空）被跳过；
 * 结尾无换行的残留缓冲若非空则作为最后一行解析。非法 JSON 行抛 `SyntaxError`
 * （由调用方决定吞还是抛——本包域模块选择向上抛，不静默吞错）。
 *
 * @param {AsyncIterable<string>} textChunks 已解码为字符串的 chunk 流
 * @returns {AsyncGenerator<any, void, void>}
 */
export async function* parseNdjsonStream(textChunks) {
  let buffer = ''
  for await (const chunk of textChunks) {
    buffer += chunk
    let nl
    while ((nl = buffer.indexOf('\n')) !== -1) {
      const raw = buffer.slice(0, nl)
      buffer = buffer.slice(nl + 1)
      const line = stripCr(raw)
      if (line.trim() === '') continue
      yield JSON.parse(line)
    }
  }
  const tail = stripCr(buffer)
  if (tail.trim() !== '') {
    yield JSON.parse(tail)
  }
}

/**
 * 把 web `ReadableStream`（`Uint8Array` chunk，来自 `Response.body`）解码为文本
 * chunk 的 async iterable，喂给 {@link parseNdjsonStream}。
 *
 * @param {ReadableStream<Uint8Array>} body
 * @returns {AsyncGenerator<string, void, void>}
 */
export async function* decodeStream(body) {
  const reader = body.getReader()
  const decoder = new TextDecoder('utf-8')
  try {
    for (;;) {
      const { value, done } = await reader.read()
      if (done) break
      if (value) yield decoder.decode(value, { stream: true })
    }
    const flushed = decoder.decode()
    if (flushed) yield flushed
  } finally {
    reader.releaseLock()
  }
}

/**
 * @param {string} s
 * @returns {string}
 */
function stripCr(s) {
  return s.endsWith('\r') ? s.slice(0, -1) : s
}
